import React from 'react'
import { Box, Typography, Card, Grid, Chip } from '@mui/material'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag'
import { Order } from 'hooks/useOrder'
import { useTranslation } from 'react-i18next'
import { formatDisplayDate, formatToTitleCase } from 'utils/formatUtils'

interface OrderCardProps {
  order: Order
  onClick: (order: Order) => void
}

const getStatusColor = (
  status: string
): 'warning' | 'success' | 'info' | 'error' | 'default' => {
  switch (status) {
    case 'AWAITING_PAYMENT':
      return 'warning'
    case 'PAYMENT_PENDING':
      return 'default'
    case 'PAID':
    case 'NETSUITE_FAILED':
      return 'success'
    case 'REFUNDED':
      return 'default'
    case 'TRANSACTION_PENDING':
      return 'warning'
    case 'CLOSED':
      return 'error'
    case 'PICKED':
      return 'info'
    case 'PAYMENT_FAILED':
      return 'error'
    default:
      return 'default'
  }
}

const getStatusTranslationKey = (status: string): string | null => {
  switch (status) {
    case 'AWAITING_PAYMENT':
      return 'myAccount.orderStatus.awaitingPayment'
    case 'PAYMENT_PENDING':
      return 'myAccount.orderStatus.paymentPending'
    case 'PAID':
    case 'NETSUITE_FAILED':
      return 'myAccount.orderStatus.paid'
    case 'REFUNDED':
      return 'myAccount.orderStatus.refunded'
    case 'TRANSACTION_PENDING':
      return 'myAccount.orderStatus.transactionPending'
    case 'CLOSED':
      return 'myAccount.orderStatus.closed'
    case 'PICKED':
      return 'myAccount.orderStatus.picked'
    case 'PAYMENT_FAILED':
      return 'myAccount.orderStatus.paymentFailed'
    default:
      return null
  }
}

const OrderCard: React.FC<OrderCardProps> = ({ order, onClick }) => {
  const { t } = useTranslation()
  const {
    salesOrderNumber,
    poNumber,
    createdAt,
    OrderItems,
    finalAmount,
    status
  } = order
  const itemCount = OrderItems.reduce((sum, item) => sum + item.quantity, 0)

  const translationKey = getStatusTranslationKey(status)
  const statusLabel = translationKey
    ? t(translationKey, { defaultValue: formatToTitleCase(status) })
    : formatToTitleCase(status)

  return (
    <Card
      sx={{
        mb: 3,
        p: { xs: 2, sm: 2 },
        borderRadius: 2,
        position: 'relative',
        cursor: 'pointer',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          transform: 'translateY(-4px)',
          '& .arrow-icon': {
            transform: 'translateX(4px)',
            color: 'primary.main'
          }
        }
      }}
      onClick={() => onClick(order)}
    >
      <Grid container spacing={2} alignItems='center'>
        <Grid item xs={12} sm={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.25 }}>
            <Box
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: 2,
                flex: '0 0 auto',
                height: 48,
                width: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <ShoppingBagIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box
              sx={{
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start'
              }}
            >
              <Typography
                variant='body2'
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: 1.35,
                  m: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                #{salesOrderNumber}
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{
                  display: 'block',
                  lineHeight: 1.35,
                  m: 0,
                  mt: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {formatDisplayDate(createdAt)}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} sm={3}>
          <Box
            sx={{
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start'
            }}
          >
            <Typography
              variant='body2'
              title={poNumber || 'N/A'}
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                lineHeight: 1.35,
                m: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {poNumber || 'N/A'}
            </Typography>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{
                display: 'block',
                lineHeight: 1.35,
                m: 0,
                mt: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              PO#
            </Typography>
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          sm={2}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start'
          }}
        >
          <Typography
            variant='body2'
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              fontSize: '1.1rem',
              lineHeight: 1.35,
              m: 0
            }}
          >
            ${finalAmount.toFixed(2)}
          </Typography>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ display: 'block', lineHeight: 1.35, m: 0, mt: 0.5 }}
          >
            {itemCount}{' '}
            {t(
              itemCount > 1
                ? 'myAccount.orders.items_plural'
                : 'myAccount.orders.items'
            )}
          </Typography>
        </Grid>

        <Grid
          item
          xs={12}
          sm={4}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            gap: 2
          }}
        >
          <Chip
            label={statusLabel}
            color={getStatusColor(status)}
            sx={{
              fontWeight: 600,
              fontSize: '0.8rem',
              height: 32,
              px: 1
            }}
          />
          <ArrowForwardIosIcon
            className='arrow-icon'
            sx={{
              color: 'text.secondary',
              fontSize: 20,
              transition: 'all 0.3s'
            }}
          />
        </Grid>
      </Grid>
    </Card>
  )
}

export default OrderCard
